import { useState } from 'react'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { Grid, Skeleton } from '@mui/material'

import {
  TextInputWithHelper,
  HelperWrapper,
  MediaInput,
  ProgressSteps,
  IProgressStep,
  FormImage,
  ScannersAssociationInput,
} from 'src/components'
import { MobileAdditionButtons } from './mobile-addition-buttons.component'
import { ImpactsAssociationInput, AccountsAssociationInput, CodeInput } from '../inputs'
import {
  DescriptionRichInput,
  DescriptionSimpleInput,
} from 'src/components/products/product-form/fields'
import { useAccount } from 'src/hooks'
import { IStepForm, EAccountSubscriptionPlans } from 'src/types'

import useStyles from '../styles'
import { Locations } from 'src/components/locaions'

const progressSteps = [
  'user-profile-settings-general-title',
  'associations-header-title',
  'geo-step-title',
]

export const maxProgressSteps = progressSteps.length - 1
export const minProgressSteps = 0

interface IProps {
  isDisabled?: boolean
  locale?: string
}

const FormAddition = ({ isDisabled, locale }: IProps) => {
  const [t] = useTranslation()
  const { values, handleChange, errors, setFieldValue } = useFormikContext<IStepForm>()
  const { data: account, isLoading: isAccountLoading } = useAccount()

  const classes = useStyles()

  const [progressStep, setProgressStep] = useState<IProgressStep>({
    steps: progressSteps,
    activeIndex: minProgressSteps,
  })

  const handleNextProgressStep = () => {
    if (progressStep.activeIndex === maxProgressSteps) return

    setProgressStep((prev) => {
      return { ...prev, activeIndex: prev.activeIndex + 1 }
    })
  }

  const handlePrevProgressStep = () => {
    if (progressStep.activeIndex === minProgressSteps) return

    setProgressStep((prev) => {
      return { ...prev, activeIndex: prev.activeIndex - 1 }
    })
  }

  const freeAccount = account?.subscription_plan === EAccountSubscriptionPlans.free

  return (
    <>
      <Grid className={classes.form}>
        <Grid className={classes.formWrapper} container>
          <Grid className={classes.progressStepsWrapper}>
            <ProgressSteps progressStep={progressStep} />
          </Grid>
          {progressStep.activeIndex === 0 && (
            <>
              <Grid className={classes.mainImageWrapper}>
                <FormImage alt={values.name} />
              </Grid>
              <Grid className={classes.upperInputWrapper} container item>
                <TextInputWithHelper
                  disabled={isDisabled}
                  inputName="name"
                  handleChange={handleChange}
                  value={values.name}
                  error={errors.name ? t(errors.name + '.v') : ''}
                  inputPlaceholder={t('steps-form-name-input-placeholder.v')}
                  inputUpperLabel={
                    <HelperWrapper t={'steps-form-name-input-label'} isInputLable isMandatory />
                  }
                />
                <CodeInput />
              </Grid>
              <Grid className={classes.descriptionWrapper}>
                {isAccountLoading ? (
                  <Skeleton variant="rectangular" height={230} width="100%" />
                ) : freeAccount ? (
                  <DescriptionSimpleInput
                    isRequired={false}
                    isDisabled={isDisabled}
                    locale={locale}
                    textPrompt={`${t('steps-form-ai-prompt.v')} ${values.name || '{step name}'} ${t(
                      'products-form-ai-prompt-1.v',
                    )} ${account?.account_holder || '{account name}'} ${t(
                      'products-form-ai-prompt-2.v',
                    )} ${account?.region || '{account region}'} ${
                      account?.country || '{account country}'
                    }`}
                    generationType="Step"
                  />
                ) : (
                  <DescriptionRichInput
                    isRequired={false}
                    isDisabled={isDisabled}
                    locale={locale}
                    textPrompt={`${t('steps-form-ai-prompt.v')} ${values.name || '{step name}'} ${t(
                      'products-form-ai-prompt-1.v',
                    )} ${account?.account_holder || '{account name}'} ${t(
                      'products-form-ai-prompt-2.v',
                    )} ${account?.region || '{account region}'} ${
                      account?.country || '{account country}'
                    }`}
                    generationType="Step"
                  />
                )}
              </Grid>

              <MediaInput
                disabled={isDisabled}
                inputName="media"
                media={values.media}
                setFieldValue={setFieldValue}
                mainImageSwitch
                withDnd
              />
            </>
          )}
          {progressStep.activeIndex === 1 && (
            <>
              <ImpactsAssociationInput isDisabled={isDisabled} />

              <ScannersAssociationInput<IStepForm>
                description={
                  <HelperWrapper t="scanners-association-title" isColorInherited size={12} />
                }
                isDisabled={isDisabled}
              />

              <AccountsAssociationInput isDisabled={isDisabled} />
            </>
          )}
          {progressStep.activeIndex === 2 && (
            <>
              <Locations
                isDisabled={isDisabled}
                errors={errors}
                locations={values.locations}
                setFieldValue={setFieldValue}
              />
            </>
          )}
          <MobileAdditionButtons
            handlePrevStep={handlePrevProgressStep}
            handleNextStep={handleNextProgressStep}
            activeStep={progressStep.activeIndex}
          />
        </Grid>
      </Grid>
    </>
  )
}

export { FormAddition }
